import { useMemo } from 'react';
import { Property } from '../../types/user';
import { MonthlyStats } from '../../types/property';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from '../../utils/format';

interface PropertyReportContentProps {
  property: Property;
  monthlyStats: MonthlyStats[];
}

export const PropertyReportContent = ({ property, monthlyStats }: PropertyReportContentProps) => {
  const yearlyTotals = useMemo(() => {
    return monthlyStats.reduce((acc, stat) => ({
      revenue: acc.revenue + stat.revenue,
      cleaningCosts: acc.cleaningCosts + stat.cleaningCosts,
      inmohostCosts: acc.inmohostCosts + stat.inmohostCosts,
      profit: acc.profit + stat.profit,
      occupancyRate: acc.occupancyRate + stat.occupancyRate,
      reservations: acc.reservations + stat.reservations,
    }), {
      revenue: 0,
      cleaningCosts: 0,
      inmohostCosts: 0,
      profit: 0,
      occupancyRate: 0,
      reservations: 0,
    });
  }, [monthlyStats]);

  const averageOccupancy = monthlyStats.length > 0 
    ? Math.round(yearlyTotals.occupancyRate / monthlyStats.length) 
    : 0;

  return (
    <div className="space-y-8 p-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">{property.name}</h1>
        <p className="text-xl text-gray-600">{property.location}</p>
      </div>

      <div>
        <h2 className="text-2xl font-semibold mb-4">Resumen Anual</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500 mb-1">Facturación Total</p>
            <p className="text-2xl font-bold">{formatCurrency(yearlyTotals.revenue)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500 mb-1">Beneficio Total</p>
            <p className="text-2xl font-bold">{formatCurrency(yearlyTotals.profit)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500 mb-1">Ocupación Media</p>
            <p className="text-2xl font-bold">{averageOccupancy}%</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-semibold mb-4">Datos Mensuales</h2>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facturación</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gastos</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Beneficio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ocupación</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {monthlyStats.map((stat, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {format(new Date(stat.year, stat.month - 1), 'MMMM yyyy', { locale: es })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(stat.revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(stat.cleaningCosts + stat.inmohostCosts)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(stat.profit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {stat.occupancyRate}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-sm text-gray-500 text-right">
        Informe generado el {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}
      </div>
    </div>
  );
};